import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus, ArrowRight, X, History } from 'lucide-react';
import { calculateDynamics } from '../data/tensionData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROOT_NAMES = {
  peregrino: 'Peregrino',
  guardiao: 'Guardião',
  amante: 'Amante',
  alquimista: 'Alquimista'
};

const ROOT_COLORS = {
  peregrino: '#1B4FD8',
  guardiao: '#C084FC',
  amante: '#EC4899',
  alquimista: '#F59E0B'
};

// Reconstruir result a partir dos dados do banco
const reconstructResult = (data) => ({
  mainArchetypes: {
    peregrino: data.score_peregrino || data.score_primavera || 0,
    guardiao: data.score_guardiao || data.score_inverno || 0,
    amante: data.score_amante || data.score_verao || 0,
    alquimista: data.score_alquimista || data.score_outono || 0
  },
  dualCombinations: data.combinacoes_duais || {},
  tripleCombinations: data.combinacoes_triplas || {},
  dominantArchetype: data.arquetipo_dominante || data.estacao_dominante || 'peregrino',
  isCoracaoInteiro: data.is_coracao_inteiro || false
});

const EvolucaoEstacoes = ({ currentResult, email, onClose }) => {
  const [previousData, setPreviousData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noPrevious, setNoPrevious] = useState(false);

  useEffect(() => {
    if (!email) {
      setLoading(false);
      setNoPrevious(true);
      return;
    }

    const fetchPrevious = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/test-results/evolucao/${encodeURIComponent(email)}`);
        if (res.ok) {
          const results = await res.json();
          // Pegar o segundo resultado (o primeiro é o atual)
          if (results.length >= 2) {
            setPreviousData(reconstructResult(results[1]));
          } else {
            setNoPrevious(true);
          }
        } else {
          setNoPrevious(true);
        }
      } catch {
        setNoPrevious(true);
      }
      setLoading(false);
    };

    fetchPrevious();
  }, [email]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-slate-900 rounded-2xl p-8 text-center border border-indigo-500/20 max-w-md w-full">
          <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/70">Buscando sua bússola anterior...</p>
        </div>
      </div>
    );
  }

  if (noPrevious) {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-slate-900 rounded-2xl p-8 text-center border border-indigo-500/20 max-w-md w-full" data-testid="evolucao-sem-anterior">
          <History className="w-12 h-12 text-amber-400/50 mx-auto mb-4" />
          <h3 className="text-white text-lg font-bold mb-2">Esta é sua primeira bússola</h3>
          <p className="text-white/60 text-sm leading-relaxed mb-6">
            Quando você refizer o teste no futuro, poderá ver aqui como suas estações evoluíram. Cada provação transforma o mapa — e essa ferramenta mostrará o caminho percorrido.
          </p>
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-all text-sm"
            data-testid="evolucao-fechar"
          >
            Entendi
          </button>
        </div>
      </div>
    );
  }

  // Temos resultado anterior — calcular diferenças
  const prev = previousData.mainArchetypes;
  const curr = currentResult.mainArchetypes;
  const prevDynamics = calculateDynamics(previousData);
  const currDynamics = calculateDynamics(currentResult);

  const diffs = Object.keys(ROOT_NAMES).map(key => ({
    key,
    name: ROOT_NAMES[key],
    prev: prev[key],
    curr: curr[key],
    diff: curr[key] - prev[key]
  }));

  // Tensões que se resolveram (estavam antes, não estão agora)
  const prevTensionIds = new Set(prevDynamics.tensions.map(t => t.id));
  const currTensionIds = new Set(currDynamics.tensions.map(t => t.id));
  const resolved = prevDynamics.tensions.filter(t => !currTensionIds.has(t.id));
  const newTensions = currDynamics.tensions.filter(t => !prevTensionIds.has(t.id));

  // Estações que despertaram (eram silenciosas, agora ativas)
  const awakened = diffs.filter(d => d.prev < 25 && d.curr >= 25);
  const silenced = diffs.filter(d => d.prev >= 25 && d.curr < 25);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-gradient-to-br from-slate-900 to-indigo-950 rounded-2xl border border-indigo-500/20 max-w-lg w-full my-8 relative" data-testid="evolucao-comparacao">
        {/* Fechar */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
          data-testid="evolucao-fechar"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="text-center">
            <History className="w-10 h-10 text-amber-400 mx-auto mb-3" />
            <h3 className="text-xl font-bold text-white">Evolução das Estações</h3>
            <p className="text-white/50 text-sm mt-1">O que mudou desde a última bússola</p>
          </div>

          {/* Comparação dos 4 eixos */}
          <div className="space-y-3">
            {diffs.map(d => {
              const pctPrev = Math.round((d.prev / 40) * 100);
              const pctCurr = Math.round((d.curr / 40) * 100);
              const isUp = d.diff > 0;
              const isDown = d.diff < 0;
              const isStable = d.diff === 0;

              return (
                <div key={d.key} className="bg-white/5 rounded-xl p-4 border border-white/10" data-testid={`evolucao-${d.key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-semibold text-sm" style={{ color: ROOT_COLORS[d.key] }}>{d.name}</span>
                    <div className="flex items-center gap-2">
                      {isUp && <TrendingUp className="w-4 h-4 text-emerald-400" />}
                      {isDown && <TrendingDown className="w-4 h-4 text-red-400" />}
                      {isStable && <Minus className="w-4 h-4 text-white/30" />}
                      <span className={`text-xs font-bold ${
                        isUp ? 'text-emerald-400' : isDown ? 'text-red-400' : 'text-white/30'
                      }`}>
                        {isUp ? '+' : ''}{d.diff}
                      </span>
                    </div>
                  </div>

                  {/* Barras comparativas */}
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-white/30 text-xs w-12">Antes</span>
                      <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pctPrev}%`, backgroundColor: ROOT_COLORS[d.key], opacity: 0.35 }} />
                      </div>
                      <span className="text-white/30 text-xs w-6 text-right">{d.prev}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-white/50 text-xs w-12">Agora</span>
                      <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pctCurr}%`, backgroundColor: ROOT_COLORS[d.key] }} />
                      </div>
                      <span className="text-white/50 text-xs w-6 text-right">{d.curr}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Insights de mudança */}
          <div className="space-y-3">
            {/* Estações que despertaram */}
            {awakened.length > 0 && (
              <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/20" data-testid="evolucao-despertaram">
                <p className="text-emerald-300 text-sm font-semibold mb-1">
                  {awakened.length === 1 ? 'Estação que despertou' : 'Estações que despertaram'}
                </p>
                <p className="text-white/70 text-sm leading-relaxed">
                  {awakened.map(a => a.name).join(' e ')} {awakened.length === 1 ? 'cruzou' : 'cruzaram'} o limiar — {awakened.length === 1 ? 'essa estação agora está' : 'essas estações agora estão'} ativa{awakened.length > 1 ? 's' : ''} no seu mapa.
                </p>
              </div>
            )}

            {/* Estações que silenciaram */}
            {silenced.length > 0 && (
              <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/20" data-testid="evolucao-silenciaram">
                <p className="text-amber-300 text-sm font-semibold mb-1">
                  {silenced.length === 1 ? 'Estação que silenciou' : 'Estações que silenciaram'}
                </p>
                <p className="text-white/70 text-sm leading-relaxed">
                  {silenced.map(s => s.name).join(' e ')} {silenced.length === 1 ? 'recuou' : 'recuaram'} — {silenced.length === 1 ? 'essa estação pede' : 'essas estações pedem'} atenção renovada.
                </p>
              </div>
            )}

            {/* Tensões resolvidas */}
            {resolved.length > 0 && (
              <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/20" data-testid="evolucao-resolvidas">
                <p className="text-blue-300 text-sm font-semibold mb-1">
                  {resolved.length === 1 ? 'Tensão que se dissolveu' : 'Tensões que se dissolveram'}
                </p>
                <p className="text-white/70 text-sm leading-relaxed">
                  {resolved.map(t => `${t.eixoA.name} × ${t.eixoB.name}`).join(', ')} — {resolved.length === 1 ? 'esse eixo encontrou' : 'esses eixos encontraram'} mais equilíbrio.
                </p>
              </div>
            )}

            {/* Novas tensões */}
            {newTensions.length > 0 && (
              <div className="bg-orange-500/10 rounded-xl p-4 border border-orange-500/20" data-testid="evolucao-novas-tensoes">
                <p className="text-orange-300 text-sm font-semibold mb-1">
                  {newTensions.length === 1 ? 'Nova tensão surgiu' : 'Novas tensões surgiram'}
                </p>
                <p className="text-white/70 text-sm leading-relaxed">
                  {newTensions.map(t => `${t.eixoA.name} × ${t.eixoB.name}`).join(', ')} — {newTensions.length === 1 ? 'uma nova dinâmica pede' : 'novas dinâmicas pedem'} atenção no seu caminho.
                </p>
              </div>
            )}

            {/* Sem mudanças significativas */}
            {awakened.length === 0 && silenced.length === 0 && resolved.length === 0 && newTensions.length === 0 && (
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <p className="text-white/60 text-sm leading-relaxed text-center">
                  Sua configuração se manteve estável. As mesmas forças e tensões continuam presentes — o que pode indicar consistência ou a necessidade de novas experiências para movimentar o mapa.
                </p>
              </div>
            )}
          </div>

          {/* Mensagem final */}
          <p className="text-white/40 text-xs text-center italic leading-relaxed">
            Cada provação transforma o mapa. Continue refazendo o teste para acompanhar sua jornada emocional.
          </p>

          <div className="text-center">
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all text-sm"
              data-testid="evolucao-fechar-btn"
            >
              Voltar ao resultado
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvolucaoEstacoes;
